import axios from 'axios'
import store from '@/store'
import { has } from 'lodash'
import { authClientInstance } from '@/auth/AuthClient'

let isRefreshing = false
let pendingRequests = []

const axiosInstance = axios.create({
  baseURL: process.env.VUE_APP_URL_API,
  timeout: process.env.VUE_APP_API_TIMEOUT
})

const auth = async (config) => {
  const user = await authClientInstance.getUser()
  store.commit('auth/setAuthUserInfo', user)
  const authUserInfo = store.getters['auth/getAuthUserInfo']
  if (has(authUserInfo, 'access_token') && has(authUserInfo, 'token_type')) {
    if (!has(config, 'headers')) {
      config.headers = {}
    }
    config.headers.Authorization = authUserInfo.token_type + ' ' + authUserInfo.access_token
  }

  return config
}

const refreshTokenOrFail = (error) => {
  const { config, response: { status } } = error
  const originalRequest = config

  if (status === 401) {
    if (isRefreshing) {
      return new Promise((resolve) => {
        return addPendingRequest((token) => {
          originalRequest.headers.Authorization = token

          return resolve(axios.request(originalRequest))
        })
      })
    }
    return retry(originalRequest)
  }

  return Promise.reject(error)
}

const retry = async (originalRequest) => {
  isRefreshing = true

  try {
    const response = await authClientInstance.refreshToken()
    const user = await authClientInstance.getUser()
    store.commit('auth/setAuthUserInfo', user)
    if (response.status === 200) {
      const authUserInfo = store.getters['auth/getAuthUserInfo']
      const accessToken = authUserInfo.token_type + ' ' + authUserInfo.access_token
      retryPendingWithNewToken(accessToken)

      originalRequest.headers.Authorization = accessToken
      isRefreshing = false
      pendingRequests = []

      return axios.request(originalRequest)
    }
  } catch (error) {
    isRefreshing = false
    clearTokensAndReturnToLogin()
  }
}

const addPendingRequest = (callback) => {
  pendingRequests.push(callback)
}

const retryPendingWithNewToken = (accessToken) => {
  pendingRequests = pendingRequests.filter(callback => callback(accessToken))
}

const clearTokensAndReturnToLogin = () => {
  store.dispatch('auth/logout')
}

axiosInstance.interceptors.request.use(auth, error => Promise.reject(error))
axiosInstance.interceptors.response.use(response => response, refreshTokenOrFail)

export default axiosInstance
